import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList, ViewChild,
    ViewChildren
} from "@angular/core";
import { NuveiApplicationComponent } from "./nuvei-application.component";
import { AbstractControl, FormGroup } from "@angular/forms";
import { Observable, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { PlacesService } from '../../../../../common/src/lib/services/places.service';
import { UtilsService } from "../../../../../common/src/lib/services";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { digitsAndDash, onDigitsAndDashPaste } from "../../helpers/forms";

export const providers = [
    { provide: MAT_DATE_FORMATS, useValue: {
            parse: {
                dateInput: 'MM/DD/YYYY',
            },
            display: {
                dateInput: 'MM/DD/YYYY',
                monthYearLabel: 'MMM YYYY',
                dateA11yLabel: 'LL',
                monthYearA11yLabel: 'MMMM YYYY',
                monthLabel: 'MMMM',
            },
        } },
];

export function smoothScrollIntoView(element: HTMLElement, containerElement: HTMLElement) {
    const container = containerElement;
    const containerHeight = container.clientHeight;
    const containerScrollTop = container.scrollTop;

    const elementTop = element.offsetTop;
    const elementHeight = element.offsetHeight;

    const elementCenter = elementTop + elementHeight / 2;
    const containerCenter = containerScrollTop + containerHeight / 2;

    const offset = elementCenter - containerCenter;

    // Only adjust scroll if the item is too close to the top or bottom
    if (Math.abs(offset) > containerHeight / 4) {
        container.scrollTo({
            top: containerScrollTop + offset,
            behavior: 'smooth',
        });
    }
}

export type NuveiApplicationComponentFormName = {
    [K in keyof NuveiApplicationComponent]: NuveiApplicationComponent[K] extends FormGroup ? K : never;
}[keyof NuveiApplicationComponent];

@Component({
    standalone: true,
    template: ''
})
export abstract class ApplicationStep<T extends NuveiApplicationComponentFormName> implements OnInit, AfterViewInit, OnDestroy {

    @ViewChildren('itemDiv') itemDivs!: QueryList<ElementRef>;
    @ViewChild('containerDiv') containerDiv!: ElementRef;

    bgStyles = {
        top: '0px',
        left: '0px',
        width: '0px',
        height: '0px',
        opacity: '0'
    };
    activeIndex: number | null = null;

    @Input() form!: NuveiApplicationComponent[T];
    @Input() progress!: number;

    @Output() formUpdate = new EventEmitter();
    @Output() nextClick = new EventEmitter();

    focusedFields: { [field: string]: boolean } = {};
    formSub?: Subscription;

    protected abstract document: Document;
    protected utilsService?: UtilsService;
    protected placesService?: PlacesService;
    protected abstract fieldExplanations: {
        id: string,
        title: string,
        explanation: string,
    }[];
    protected fieldToExplanations?: {
        [field: string]: string
    };

    digitsAndDash = digitsAndDash;
    onDigitsAndDashPaste = onDigitsAndDashPaste;

    ngOnInit() {
        // @ts-ignore
        this.formSub = this.form.valueChanges.pipe(debounceTime(1000)).subscribe(value => {
            this.formUpdate.emit();
        });
    }

    async ngAfterViewInit() {
        this.initExplanationBackground();
    }

    formControlError(form: FormGroup, field: string) {
        const errors = form.controls[field].errors;
        if(!errors)
            return null;
        return Object.keys(errors).find(error => !!errors[error]) ?? null;
    }

    addressError(form: FormGroup, field: string, fieldName = 'Address') {
        const errorCode = this.formControlError(form, field);
        return {
            'required': `${fieldName} is required`,
            'addressIsString': 'Select a full address option from the dropdown',
            'missingComponents': 'Select a full address option from the dropdown',
            'usOnly': 'Allowed only for US addresses',
        }[errorCode ?? ''];
    }

    maskInput(event: Event, control: AbstractControl, mask: string) {
        this.utilsService!.maskInput(event.target as HTMLInputElement, control, mask);
    }

    showOptionalField(field: string, checkedToShow = false) {
        const checked = (this.form as FormGroup).get(field)?.value ?? false;
        return checkedToShow ? checked : !checked;
    }

    activeExplanationChange(fieldId: string, focused: boolean) {
        this.focusedFields[fieldId] = focused;
        this.updateFocusedExplanation();
    }

    getExplanationForField(fieldId: string) {
        if(this.fieldToExplanations) {
            return this.fieldToExplanations[fieldId] ?? fieldId;
        }
        return fieldId;
    }

    initExplanationBackground() {
        const activeElement = this.itemDivs.toArray()[0].nativeElement;
        const containerRect = this.containerDiv.nativeElement.getBoundingClientRect();
        const rect = activeElement.getBoundingClientRect();
        const containerScrollTop = this.containerDiv.nativeElement.scrollTop;

        this.bgStyles = {
            top: `${rect.top - containerRect.top + containerScrollTop - 8}px`,
            left: `${rect.left - containerRect.left - 8}px`,
            width: `${rect.width + 8}px`,
            height: `${rect.height + 16}px`,
            opacity: '0'
        };
    }

    updateFocusedExplanation() {
        const activeFieldId = Object.keys(this.focusedFields).find(fieldId => this.focusedFields[fieldId]);

        if (!activeFieldId) {
            this.activeIndex = null;
            this.bgStyles = { ...this.bgStyles, opacity: '0' };
            return;
        }
        const explanationId = this.getExplanationForField(activeFieldId);
        this.activeIndex = this.fieldExplanations.findIndex(explanation => explanation.id === explanationId);
        smoothScrollIntoView(this.document.getElementById('nuvei_app_' + explanationId)!, this.containerDiv.nativeElement);

        const activeElement = this.itemDivs.toArray()[this.activeIndex].nativeElement;
        const containerRect = this.containerDiv.nativeElement.getBoundingClientRect();
        const rect = activeElement.getBoundingClientRect();
        const containerScrollTop = this.containerDiv.nativeElement.scrollTop;

        this.bgStyles = {
            top: `${rect.top - containerRect.top + containerScrollTop - 8}px`,
            left: `${rect.left - containerRect.left - 8}px`,
            width: `${rect.width + 8}px`,
            height: `${rect.height + 16}px`,
            opacity: '1'
        };
    }

    clearAddress(index?: number) {
        const control = index === undefined
            ? (this.form as FormGroup).controls['address']
            : (this.form as NuveiApplicationComponent['businessOwnersForm']).controls.owners.controls[index].controls.address
        control.setValue(null);
    }

    fixDate(field: string, form: FormGroup) {
        const control = form.controls[field];
        if(typeof control.value === 'string') {
            const date = new Date(control.value);
            control.setValue(date);
        }
    }

    min(value: number, field: string, form: FormGroup) {
        const control = form.controls[field];
        if(control.value < value)
            control.setValue(value);
    }

    max(value: number, field: string, form: FormGroup) {
        const control = form.controls[field];
        if(control.value > value)
            control.setValue(value);
    }

    minMax(min: number, max: number, field: string, form: FormGroup) {
        if(typeof form.controls[field].value !== 'number')
            return;
        this.min(min, field, form);
        this.max(max, field, form);
    }

    fixDashedNumber(field: string, form: FormGroup) {
        const value = form.controls[field].value;
        if (value.split('').pop() === '-')
            form.controls[field].setValue(value.slice(0, -1));
    }

    ngOnDestroy() {
        this.formUpdate.emit();
        this.formSub?.unsubscribe();
    }

}